import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { 
  X, 
  Trash2, 
  RotateCcw, 
  AlertCircle,
  FileText,
  FormInput,
  Link as LinkIcon,
  Workflow,
  Database,
  ExternalLink,
  Clock,
  User,
  MapPin,
  Grid,
  Eye,
  EyeOff
} from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { api } from '../lib/api';
import { showAlert, showConfirm, showPrompt } from './common/AppDialogs';

interface DeletedItemsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface DeletedItem {
  id: string;
  type: string;
  title: string;
  description: string;
  page: string;
  pageId: string;
  pageDeleted: boolean;
  column: number;
  columnSpan: number;
  row: number;
  rowSpan: number;
  deletedBy: string;
  deletedByEmail: string;
  deletedAt: string;
  itemType: 'tile' | 'page';
  originalData: any;
}

const tileTypeIcons = {
  content: FileText,
  form: FormInput,
  shortcut: LinkIcon,
  workflow: Workflow,
  register: Database,
  external_link: ExternalLink,
  nested_page: FileText,
  page: Grid,
};

const tileTypeLabels = {
  content: 'Content',
  form: 'Form',
  shortcut: 'Shortcut',
  workflow: 'Workflow', 
  register: 'Register',
  external_link: 'External Link',
  nested_page: 'Page',
  page: 'Page',
};

export default function DeletedItemsModal({ isOpen, onClose }: DeletedItemsModalProps) {
  const [showUserItemsOnly, setShowUserItemsOnly] = useState(false);
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());
  const queryClient = useQueryClient();

  // Fetch deleted items
  const { 
    data: deletedItemsData, 
    isLoading, 
    error,
    refetch 
  } = useQuery(
    ['deleted-items', showUserItemsOnly],
    () => api.get(`/deleted-items?userOnly=${showUserItemsOnly}`).then(res => res.data),
    { 
      enabled: isOpen,
      refetchOnWindowFocus: false 
    }
  );

  // Restore item mutation
  const restoreMutation = useMutation(
    ({ itemType, id, targetPageId }: { itemType: string, id: string, targetPageId?: string }) =>
      api.post(`/deleted-items/${itemType}/${id}/restore`, targetPageId ? { targetPageId } : {}),
    {
      onSuccess: (data) => {
        // Refresh the deleted items list
        refetch();
        
        // Extract response data
        const responseData = data.data?.data;
        const restoredPageId = responseData?.restoredToPageId;
        
        if (restoredPageId) {
          // Force complete cache refresh to ensure restored tile appears immediately
          // This triggers RESET_STATE in PageView which updates GridContext
          queryClient.removeQueries(['page', restoredPageId]);
          queryClient.refetchQueries(['page', restoredPageId], { 
            active: true, 
            inactive: true,
            stale: true
          });
        }
        
        // Invalidate the general pages list queries
        queryClient.invalidateQueries('all-pages');
        queryClient.invalidateQueries('homepage-nav');
        
        // Create informative success message
        const pageName = responseData?.restoredToPage || 'original location';
        const position = responseData?.restoredPosition;
        
        let message = `Item restored successfully to "${pageName}"`;
        if (position) {
          message += ` at row ${position.row}, column ${position.column}`;
        }
        
        showAlert(message);
      },
      onError: (error: any) => {
        showAlert(`Failed to restore item: ${error.response?.data?.error || error.message}`);
      }
    }
  );

  // Permanent delete mutation
  const permanentDeleteMutation = useMutation(
    ({ itemType, id }: { itemType: string, id: string }) =>
      api.delete(`/deleted-items/${itemType}/${id}/permanent`),
    {
      onSuccess: () => {
        refetch();
        showAlert('Item permanently deleted');
      },
      onError: (error: any) => {
        showAlert(`Failed to permanently delete item: ${error.response?.data?.error || error.message}`);
      }
    }
  );

  const extendRetentionMutation = useMutation(
    ({ itemType, id }: { itemType: string, id: string }) =>
      api.post(`/deleted-items/${itemType}/${id}/extend-retention`),
    {
      onSuccess: (data) => {
        refetch();
        const responseData = data.data?.data;
        showAlert(`Data retention extended successfully for "${responseData?.title}". The item now has a full retention period from today.`);
      },
      onError: (error: any) => {
        showAlert(`Failed to extend retention: ${error.response?.data?.error || error.message}`);
      }
    }
  );

  const deletedItems: DeletedItem[] = deletedItemsData?.data || [];

  const handleRestore = async (item: DeletedItem) => {
    if (item.pageDeleted) {
      // TODO: Show page selection modal
      const targetPageId = await showPrompt('The original page was deleted. Enter the ID of the page to restore to:');
      if (targetPageId) {
        restoreMutation.mutate({ itemType: item.itemType, id: item.id, targetPageId });
      }
    } else {
      restoreMutation.mutate({ itemType: item.itemType, id: item.id });
    }
  };

  const handlePermanentDelete = async (item: DeletedItem) => {
    if (await showConfirm(`Are you sure you want to permanently delete "${item.title}"? This action cannot be undone and the item will be lost forever.`)) {
      permanentDeleteMutation.mutate({ itemType: item.itemType, id: item.id });
    }
  };

  const handleExtendRetention = async (item: DeletedItem) => {
    if (await showConfirm(`Extend data retention for "${item.title}"? This will restart the retention period from today, giving you the full retention time before the item is automatically deleted.`)) {
      extendRetentionMutation.mutate({ itemType: item.itemType, id: item.id });
    }
  };

  const handleSelectItem = (id: string) => {
    const newSelected = new Set(selectedItems);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedItems(newSelected);
  };

  const handleSelectAll = () => {
    if (selectedItems.size === deletedItems.length) {
      setSelectedItems(new Set());
    } else {
      setSelectedItems(new Set(deletedItems.map(item => item.id)));
    }
  };

  // Close modal on escape key
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  const modalContent = (
    <div 
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="bg-card rounded-lg shadow-xl w-full max-w-7xl h-[85vh] flex flex-col overflow-hidden border border-border">
        {/* Header */}
        <div className="p-6 border-b border-border bg-card">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-red-100 rounded-lg">
                <Trash2 className="h-5 w-5 text-red-600" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-card-foreground">Deleted Items</h2>
                <p className="text-sm text-muted-foreground">
                  Manage and restore deleted tiles and pages
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-muted rounded-lg transition-colors text-muted-foreground hover:text-foreground"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Filters and Actions */}
          <div className="flex items-center justify-between mt-4">
            <div className="flex items-center space-x-4">
              <label className="flex items-center space-x-2 text-sm">
                <input
                  type="checkbox"
                  checked={showUserItemsOnly}
                  onChange={(e) => setShowUserItemsOnly(e.target.checked)}
                  className="rounded border-border"
                />
                <span>Show only my deleted items</span>
              </label>
              
              {deletedItems.length > 0 && (
                <div className="text-sm text-muted-foreground">
                  {deletedItems.length} item{deletedItems.length !== 1 ? 's' : ''} found
                </div>
              )}
            </div>

            {selectedItems.size > 0 && (
              <div className="flex items-center space-x-2">
                <span className="text-sm text-muted-foreground">
                  {selectedItems.size} selected
                </span>
                <button
                  onClick={() => {
                    // TODO: Implement bulk restore
                    showAlert('Bulk restore not yet implemented');
                  }}
                  className="px-3 py-1 text-xs bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  Restore Selected
                </button>
                <button
                  onClick={() => {
                    // TODO: Implement bulk permanent delete
                    showAlert('Bulk permanent delete not yet implemented');
                  }}
                  className="px-3 py-1 text-xs bg-red-600 text-white rounded-md hover:bg-red-700"
                >
                  Delete Selected
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-hidden">
          {isLoading ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-muted-foreground">Loading deleted items...</div>
            </div>
          ) : error ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-red-600">Error loading deleted items</div>
            </div>
          ) : deletedItems.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
              <Trash2 className="h-12 w-12 mb-4 opacity-50" />
              <h3 className="text-lg font-medium mb-2">No deleted items</h3>
              <p className="text-sm">
                {showUserItemsOnly 
                  ? "You haven't deleted any items yet"
                  : "No items have been deleted in this organization"
                }
              </p>
            </div>
          ) : (
            <div className="overflow-auto h-full">
              <table className="w-full">
                <thead className="sticky top-0 bg-muted/50 border-b border-border">
                  <tr>
                    <th className="text-left p-3 font-medium text-sm">
                      <input
                        type="checkbox"
                        checked={selectedItems.size === deletedItems.length && deletedItems.length > 0}
                        onChange={handleSelectAll}
                        className="rounded border-border"
                      />
                    </th>
                    <th className="text-left p-3 font-medium text-sm">Title</th>
                    <th className="text-left p-3 font-medium text-sm">Type</th>
                    <th className="text-left p-3 font-medium text-sm">Page</th>
                    <th className="text-left p-3 font-medium text-sm">Position</th>
                    <th className="text-left p-3 font-medium text-sm">Size</th>
                    <th className="text-left p-3 font-medium text-sm">Deleted By</th>
                    <th className="text-left p-3 font-medium text-sm">Date Deleted</th>
                    <th className="text-left p-3 font-medium text-sm">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {deletedItems.map((item) => {
                    const IconComponent = tileTypeIcons[item.type] || FileText;
                    
                    return (
                      <tr key={item.id} className="border-b border-border hover:bg-muted/50">
                        <td className="p-3">
                          <input
                            type="checkbox"
                            checked={selectedItems.has(item.id)}
                            onChange={() => handleSelectItem(item.id)}
                            className="rounded border-border"
                          />
                        </td>
                        <td className="p-3">
                          <div className="flex items-center space-x-2">
                            <IconComponent className="h-4 w-4 text-muted-foreground" />
                            <div>
                              <div className="font-medium text-sm">{item.title}</div>
                              {item.description && (
                                <div className="text-xs text-muted-foreground truncate max-w-xs">
                                  {item.description}
                                </div>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="p-3">
                          <span className="text-sm">
                            {tileTypeLabels[item.type] || item.type}
                          </span>
                        </td>
                        <td className="p-3">
                          <div className="flex items-center space-x-1">
                            <span className="text-sm">{item.page}</span>
                            {item.pageDeleted && (
                              <AlertCircle className="h-3 w-3 text-red-500" aria-label="Original page was deleted" />
                            )}
                          </div>
                        </td>
                        <td className="p-3">
                          <div className="text-sm text-muted-foreground">
                            {item.itemType === 'page' ? 'N/A' : `R${item.row} C${item.column}`}
                          </div>
                        </td>
                        <td className="p-3">
                          <div className="text-sm text-muted-foreground">
                            {item.itemType === 'page' ? 'N/A' : `${item.columnSpan}×${item.rowSpan}`}
                          </div>
                        </td>
                        <td className="p-3">
                          <div className="text-sm">{item.deletedBy}</div>
                        </td>
                        <td className="p-3">
                          <div className="text-sm text-muted-foreground">
                            {formatDate(item.deletedAt)}
                          </div>
                        </td>
                        <td className="p-3">
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={() => handleRestore(item)}
                              disabled={restoreMutation.isLoading}
                              className="p-1 hover:bg-green-100 rounded text-green-600 hover:text-green-700 disabled:opacity-50"
                              title="Restore item"
                            >
                              <RotateCcw className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => handleExtendRetention(item)}
                              disabled={extendRetentionMutation.isLoading}
                              className="p-1 hover:bg-blue-100 rounded text-blue-600 hover:text-blue-700 disabled:opacity-50"
                              title="Extend data retention (restart timer)"
                            >
                              <Clock className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => handlePermanentDelete(item)}
                              disabled={permanentDeleteMutation.isLoading}
                              className="p-1 hover:bg-red-100 rounded text-red-600 hover:text-red-700 disabled:opacity-50"
                              title="Permanently delete"
                            >
                              <X className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-border bg-muted/30">
          <div className="flex items-center justify-between">
            <div className="text-sm text-muted-foreground">
              Items in the deleted items list will be automatically removed based on your organization's data retention policy.
            </div>
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-foreground bg-card border border-border rounded-md hover:bg-muted transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
}
